import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReferenceImage } from '@historyjki/shared';
import { r2Url } from '../../utils/r2Url';

interface ImageGalleryProps {
  open: boolean;
  onClose: () => void;
  images: ReferenceImage[];
  initialIndex: number;
}

export function ImageGallery({
  open,
  onClose,
  images,
  initialIndex,
}: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setCurrentIndex(initialIndex);
  }, [open, initialIndex]);

  const total = images.length;
  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + total) % total);
  }, [total]);
  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % total);
  }, [total]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (total > 1 && e.key === 'ArrowLeft') goPrev();
      else if (total > 1 && e.key === 'ArrowRight') goNext();
    },
    [onClose, goPrev, goNext, total],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open || total === 0) return null;

  const current = images[Math.min(currentIndex, total - 1)];

  return createPortal(
    <div
      className="image-gallery__overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
    >
      <button
        type="button"
        className="image-gallery__close"
        onClick={onClose}
        aria-label="Close gallery"
      >
        <span aria-hidden="true">&times;</span>
        <span>Close</span>
      </button>
      {total > 1 && (
        <button
          type="button"
          className="image-gallery__nav image-gallery__nav--prev"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          aria-label="Previous image"
        >
          <span aria-hidden="true">‹</span>
          <span>Prev</span>
        </button>
      )}
      <figure
        className="image-gallery__figure"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={r2Url(current.r2_key)}
          alt={current.filename}
          className="image-gallery__image"
        />
        <figcaption className="image-gallery__caption">
          <span className="image-gallery__filename">{current.filename}</span>
          {total > 1 && (
            <span className="image-gallery__counter">
              {currentIndex + 1} / {total}
            </span>
          )}
        </figcaption>
      </figure>
      {total > 1 && (
        <button
          type="button"
          className="image-gallery__nav image-gallery__nav--next"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          aria-label="Next image"
        >
          <span>Next</span>
          <span aria-hidden="true">›</span>
        </button>
      )}
    </div>,
    document.body,
  );
}
